import { FC } from 'react';

import CompanyLogoLightSVG from '@/assets/svgs/logos/logo-light.svg?raw';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { centerComponentInMiddleofViewWithoutAlignItems } from '@/utilities/styles/centerInMiddle';

const logoPathData = [...CompanyLogoLightSVG.matchAll(/<path\b[^>]*\sd="([^"]+)"/g)]
    .map((match) => match[1]?.replace(/\s+/g, ' ').trim())
    .filter((path): path is string => Boolean(path));

const AnimatedLogo: FC = (): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <svg
            aria-label='Countercyclical'
            role='img'
            viewBox='0 0 398 394'
            width='128'
            height='128'
            preserveAspectRatio='xMidYMid meet'
            style={{ color: token.colorBgTextActive, display: 'block' }}
        >
            <style>
                {`
                    .countercyclical-loading-logo-path {
                        animation: countercyclical-logo-draw 2.2s ease-in-out infinite alternate;
                    }

                    @keyframes countercyclical-logo-draw {
                        0% {
                            opacity: 0.5;
                            stroke-dashoffset: 1;
                        }

                        100% {
                            opacity: 1;
                            stroke-dashoffset: 0;
                        }
                    }

                    @media (prefers-reduced-motion: reduce) {
                        .countercyclical-loading-logo-path {
                            animation: none;
                            opacity: 1;
                            stroke-dashoffset: 0;
                        }
                    }
                `}
            </style>

            <g transform='translate(0,394) scale(0.1,-0.1)'>
                {logoPathData.map((d, index) => (
                    <path
                        className='countercyclical-loading-logo-path'
                        key={d}
                        d={d}
                        pathLength={1}
                        stroke='currentColor'
                        strokeWidth={32}
                        fill='none'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeDasharray={1}
                        strokeDashoffset={1}
                        style={{ animationDelay: `${index * 45 + Math.sin(index) * 10}ms` }}
                    />
                ))}
            </g>
        </svg>
    );
};

const FullscreenLoading: FC = (): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <div
            style={{
                backgroundColor: token.colorBgContainer,
                height: '100%',
                width: '100%',
                ...centerComponentInMiddleofViewWithoutAlignItems,
                overflow: 'hidden',
                backgroundImage: `radial-gradient(
                    circle at 0.125rem 0.125rem,
                    ${token.colorBorder} 0.125rem,
                    transparent 0
                    )`,
                backgroundSize: '1.5rem 1.5rem',
            }}
        >
            <div style={{ margin: '0 auto', textAlign: 'center' }}>
                <AnimatedLogo />
            </div>
        </div>
    );
};

export default FullscreenLoading;
